import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";

import UserSearchPicker from "../common/UserSearchPicker/UserSearchPicker";
import GetAllAdmins from "../../utils/database/GetAllAdmins";
import SetUserRole from "../../utils/database/SetUserRole";
import useUserDetails from "../../utils/auth/UserDetails";

const AdminManagementPage = () => {
  const userDetails = useUserDetails();
  const adminUserId = userDetails?.userId;

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pickerSelection, setPickerSelection] = useState([]);
  const [promoting, setPromoting] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const refresh = useCallback(async () => {
    if (!adminUserId) return;
    setLoading(true);
    setError("");
    const res = await GetAllAdmins(adminUserId);
    if (!res.success) {
      setError(res.error || "Failed to load admins");
      setAdmins([]);
    } else {
      setAdmins(res.admins || []);
    }
    setLoading(false);
  }, [adminUserId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handlePromote = async () => {
    if (pickerSelection.length === 0 || !adminUserId) return;
    setPromoting(true);
    const failed = [];
    const succeeded = [];
    for (const user of pickerSelection) {
      const res = await SetUserRole(adminUserId, user.id, "admin");
      if (res.success) {
        succeeded.push(user);
      } else {
        failed.push({ user, error: res.error });
      }
    }
    setPromoting(false);
    setPickerSelection([]);
    if (succeeded.length > 0) {
      toast.success(
        succeeded.length === 1
          ? `Promoted ${succeeded[0].displayName || succeeded[0].email} to admin`
          : `Promoted ${succeeded.length} users to admin`
      );
    }
    if (failed.length > 0) {
      toast.error(
        `Failed to promote ${failed.length} user${failed.length > 1 ? "s" : ""}: ${failed[0].error}`
      );
    }
    refresh();
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget || !adminUserId) return;
    setRevoking(true);
    const res = await SetUserRole(adminUserId, revokeTarget.id, "user");
    setRevoking(false);
    if (res.success) {
      toast.success(`Revoked admin from ${revokeTarget.displayName || revokeTarget.email}`);
      setRevokeTarget(null);
      refresh();
    } else {
      toast.error(`Failed to revoke admin: ${res.error}`);
    }
  };

  const isSelf = (user) => user?.id === adminUserId;

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: 900,
        mx: "auto",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AdminPanelSettingsIcon sx={{ color: "#333366", fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#333366" }}>
            Admin Management
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refresh}
          disabled={loading}
          sx={{ textTransform: "none" }}
        >
          Refresh
        </Button>
      </Box>

      <Card sx={{ mb: 3, flexShrink: 0 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333366", mb: 1 }}>
            Promote a user to admin
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
            Search by name or email, select one or more users, then promote. The user will see admin features after their next sign-in.
          </Typography>
          <UserSearchPicker
            label=""
            placeholder="Search by name or email"
            selectedUsers={pickerSelection}
            onChange={setPickerSelection}
            excludeUserId={adminUserId}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              startIcon={promoting ? <CircularProgress size={16} sx={{ color: "white" }} /> : <PersonAddIcon />}
              disabled={pickerSelection.length === 0 || promoting}
              onClick={handlePromote}
              sx={{ textTransform: "none", bgcolor: "#333366", "&:hover": { bgcolor: "#222255" } }}
            >
              {promoting
                ? "Promoting…"
                : `Promote ${pickerSelection.length || ""} to admin`.trim()}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            "&:last-child": { pb: 2 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexShrink: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333366" }}>
              Current admins
            </Typography>
            <Chip label={`${admins.length} admin${admins.length === 1 ? "" : "s"}`} size="small" />
          </Box>
          <Divider sx={{ mb: 2, flexShrink: 0 }} />

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Typography variant="body2" sx={{ color: "#d32f2f" }}>
              {error}
            </Typography>
          )}

          {!loading && !error && admins.length === 0 && (
            <Typography variant="body2" sx={{ color: "#666" }}>
              No admins found.
            </Typography>
          )}

          {!loading && !error && admins.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {admins.map((admin) => (
                <Box
                  key={admin.id || admin.email}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid #eee",
                    bgcolor: isSelf(admin) ? "#f5f5fb" : "white",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar sx={{ bgcolor: "#333366", width: 36, height: 36 }}>
                      {(admin.displayName || admin.email || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {admin.displayName || admin.email}
                        {isSelf(admin) && (
                          <Chip label="You" size="small" sx={{ ml: 1, height: 20 }} />
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        {admin.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    startIcon={<PersonRemoveIcon />}
                    disabled={isSelf(admin)}
                    onClick={() => setRevokeTarget(admin)}
                    sx={{ textTransform: "none" }}
                  >
                    {isSelf(admin) ? "Cannot revoke self" : "Revoke admin"}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
          </Box>
        </CardContent>
      </Card>

      <Dialog open={Boolean(revokeTarget)} onClose={() => !revoking && setRevokeTarget(null)}>
        <DialogTitle>Revoke admin access?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{revokeTarget?.displayName || revokeTarget?.email}</strong> will lose admin privileges.
            They will keep their existing extensions but won't be able to approve submissions or manage admins.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRevokeConfirm}
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={14} sx={{ color: "white" }} /> : null}
          >
            {revoking ? "Revoking…" : "Revoke admin"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagementPage;
